import { createFileRoute } from '@tanstack/react-router'
import { LegalPageShell } from '@/components/LegalPageShell'

export const Route = createFileRoute('/terms')({
  component: TermsPage,
})

function TermsPage() {
  return (
    <LegalPageShell title="Terms of Service" updated="September 13, 2026">
      <section className="space-y-4">
        <p className="text-sm leading-relaxed text-white/60">
          These Terms of Service govern your use of PeerPass, the workplace funding-circle platform
          that lets employees fund one another at 0% interest, secured by payroll. By creating an
          account or using the service, you agree to these terms on behalf of yourself and, where
          applicable, your organisation.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">1. About the service</h2>
        <p className="text-sm leading-relaxed text-white/60">
          PeerPass lets organisations set up funding circles in which employees contribute through
          payroll deductions and, in turn, access a share of the pooled amount when they need it.
          The capital is generated by the employees&rsquo; own peer pool &mdash; the employer does
          not lend money. We operate on a non-custodial basis and do not hold employee funds.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">2. Accounts &amp; eligibility</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/60">
          <li>You must provide accurate information and keep it current.</li>
          <li>You are responsible for maintaining the confidentiality of your credentials.</li>
          <li>
            Employee access is typically provisioned by your employer; employer admins are
            responsible for the accounts and permissions within their organisation.
          </li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">3. Roles &amp; responsibilities</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/60">
          <li>
            <strong className="text-white/80">Employers</strong> act as guarantor for payroll
            deductions and provide the secure platform. Contributions are deducted before an
            employee receives net pay, and our risk algorithms cap circle amounts against salary.
          </li>
          <li>
            <strong className="text-white/80">Employees</strong> participate anonymously in circles
            you are eligible for and are responsible for honouring the deductions you authorise.
          </li>
          <li>
            <strong className="text-white/80">No interest &mdash; ever.</strong> Employees pay 0%
            interest, 0% commissions and 0% platform fees on peer funding.
          </li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">4. Fees &amp; billing</h2>
        <p className="text-sm leading-relaxed text-white/60">
          PeerPass is a subscription service paid by the employer. Plans start with a 90-day free
          trial, are priced per employee and adjust automatically by region (regional pricing
          bands), with a monthly minimum that applies to every organisation. Certain markets may
          use a pay-as-you-go commission model on payouts instead of a subscription. Payment is
          processed by our payment processor; you remain responsible for any taxes.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">5. Acceptable use</h2>
        <p className="text-sm leading-relaxed text-white/60">
          You agree not to misuse the service, attempt to undermine its security or anonymity,
          upload malicious content, or use it for any unlawful purpose. We may suspend or terminate
          access where these terms are breached.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">6. Data &amp; privacy</h2>
        <p className="text-sm leading-relaxed text-white/60">
          How we handle personal data is described in our{' '}
          <a href="/privacy" className="text-[#00E5A0] hover:underline">
            Privacy Policy
          </a>
          . Organisations processing employee data also enter a Data Processing Agreement.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">7. Binding agreement</h2>
        <p className="text-sm leading-relaxed text-white/60">
          This page is a summary for reference. The full, binding Terms of Service and Data
          Processing Agreement &mdash; with versioning and electronic signature &mdash; are executed
          in-app by an authorised signatory. Where this summary and a signed document differ, the
          signed document controls.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">8. Disclaimers &amp; liability</h2>
        <p className="text-sm leading-relaxed text-white/60">
          The service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
          warranties of any kind, express or implied. To the maximum extent permitted by law,
          PeerPass is not liable for indirect, incidental, special, or consequential damages, or for
          events outside our reasonable control, including default or non-payment by a circle
          participant.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">9. Changes &amp; contact</h2>
        <p className="text-sm leading-relaxed text-white/60">
          We may update these terms and will post the revised version on this page with a new
          effective date. Continued use after a change constitutes acceptance. Questions?{' '}
          <a href="/contact" className="text-[#00E5A0] hover:underline">
            Contact us
          </a>.
        </p>
      </section>
    </LegalPageShell>
  )
}